/** Mongo / JSON sometimes serialize numbers as strings; BSON extended JSON possible. */
export function asFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return undefined;
    const n = Number(t);
    if (Number.isFinite(n)) return n;
    return undefined;
  }
  if (value != null && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o.$numberDouble === 'string') {
      const n = Number(o.$numberDouble);
      if (Number.isFinite(n)) return n;
    }
    if (typeof o.$numberInt === 'string' || typeof o.$numberInt === 'number') {
      const n = Number(o.$numberInt);
      if (Number.isFinite(n)) return n;
    }
    if (typeof o.$numberLong === 'string') {
      const n = Number(o.$numberLong);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

/**
 * Some clients store bbox as fractions of the page (0–1); the signing viewer
 * expects 0–100 percent. Values like w=0.18 were interpreted as 0.18% wide.
 */
export function scaleFractionalRectToPercent(
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number; scaled: boolean } {
  if (!(width > 0 && height > 0)) return { x, y, width, height, scaled: false };
  const m = Math.max(x, y, width, height);
  /** Fractions of page are all ≤1; sum of w+h is usually ≪1 for signature slots. */
  if (m <= 1 && width + height < 1.5) {
    return {
      x: x * 100,
      y: y * 100,
      width: width * 100,
      height: height * 100,
      scaled: true,
    };
  }
  return { x, y, width, height, scaled: false };
}

/** Mixed percent x/y with fractional w/h — see backend `normalizeFieldRectToPercent`. */
export function normalizeFieldRectToPercent(
  x: number | undefined,
  y: number | undefined,
  width: number,
  height: number,
): { x: number | undefined; y: number | undefined; width: number; height: number } {
  if (!(width > 0 && height > 0)) return { x, y, width, height };
  const xN = x ?? 0;
  const yN = y ?? 0;
  const maxPos = Math.max(xN, yN);
  const maxDim = Math.max(width, height);
  if (maxPos > 1 && maxDim <= 1 && width + height < 1.5) {
    return { x, y, width: width * 100, height: height * 100 };
  }
  const s = scaleFractionalRectToPercent(xN, yN, width, height);
  if (s.scaled) {
    return {
      x: x != null ? s.x : undefined,
      y: y != null ? s.y : undefined,
      width: s.width,
      height: s.height,
    };
  }
  return { x, y, width, height };
}
